// src/constants/compliance.ts
/**
 * Lex Koller constants (Issue #1000).
 *
 * Single source of truth for the enum values of the Lex Koller assessment —
 * never inline these strings in services, stores or components.
 */
import type {
  EntityType,
  LexKollerDataSource,
  LexKollerStatusValue,
  NationalityCategory,
  ResidencePermit,
} from '@/types/compliance'

export const LEX_KOLLER_STATUS: { readonly [K in LexKollerStatusValue]: K } = {
  UNRESTRICTED: 'UNRESTRICTED',
  CONDITIONAL: 'CONDITIONAL',
  RESTRICTED: 'RESTRICTED',
  NOT_APPLICABLE_INSTITUTIONAL: 'NOT_APPLICABLE_INSTITUTIONAL',
}

/**
 * Source marker of an assessment that was derived from the identity document
 * (`lex_koller_data.source`, Issue #1044) — mirrors `KYC_AI_SOURCE` of the API.
 */
export const LEX_KOLLER_SOURCE_KYC_AI: LexKollerDataSource = 'kyc_ai'

export const ENTITY_TYPE: { readonly [K in EntityType]: K } = {
  PERSON: 'PERSON',
  COMPANY: 'COMPANY',
}

export const ENTITY_TYPES: readonly EntityType[] = [ENTITY_TYPE.PERSON, ENTITY_TYPE.COMPANY]

export const NATIONALITY_CATEGORY: { readonly [K in NationalityCategory]: K } = {
  CH: 'CH',
  EU_EFTA: 'EU_EFTA',
  OTHER: 'OTHER',
}

export const NATIONALITY_CATEGORIES: readonly NationalityCategory[] = [
  NATIONALITY_CATEGORY.CH,
  NATIONALITY_CATEGORY.EU_EFTA,
  NATIONALITY_CATEGORY.OTHER,
]

/** Selectable residence permits, in display order. */
export const RESIDENCE_PERMITS: readonly ResidencePermit[] = ['C', 'B', 'G', 'L', 'NONE']

/** Swiss cantons (ISO 3166-2:CH), alphabetical. */
export const SWISS_CANTONS: readonly string[] = [
  'AG', 'AI', 'AR', 'BE', 'BL', 'BS', 'FR', 'GE', 'GL', 'GR',
  'JU', 'LU', 'NE', 'NW', 'OW', 'SG', 'SH', 'SO', 'SZ', 'TG',
  'TI', 'UR', 'VD', 'VS', 'ZG', 'ZH',
]

/**
 * ISO 3166-1 alpha-2 codes of the EU member and EFTA states.
 *
 * Used to translate a nationality delivered by the ident provider (a country
 * code) into the three categories the assessment works with. `CH` is handled
 * separately, `LI`/`IS`/`NO` are the remaining EFTA states.
 */
export const EU_EFTA_COUNTRY_CODES: readonly string[] = [
  'AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI',
  'FR', 'GR', 'HR', 'HU', 'IE', 'IS', 'IT', 'LI', 'LT', 'LU',
  'LV', 'MT', 'NL', 'NO', 'PL', 'PT', 'RO', 'SE', 'SI', 'SK',
]

/**
 * ISO 3166-1 alpha-3 → alpha-2.
 *
 * The identity document (and with it `kyc_nationality`, Issue #1044) carries
 * the three-letter code, while `Intl.DisplayNames` and the EU/EFTA list above
 * both work with the two-letter one.
 */
export const COUNTRY_ALPHA3_TO_ALPHA2: Readonly<Record<string, string>> = {
  AFG: 'AF', ALA: 'AX', ALB: 'AL', DZA: 'DZ', ASM: 'AS', AND: 'AD', AGO: 'AO', AIA: 'AI',
  ATA: 'AQ', ATG: 'AG', ARG: 'AR', ARM: 'AM', ABW: 'AW', AUS: 'AU', AUT: 'AT', AZE: 'AZ',
  BHS: 'BS', BHR: 'BH', BGD: 'BD', BRB: 'BB', BLR: 'BY', BEL: 'BE', BLZ: 'BZ', BEN: 'BJ',
  BMU: 'BM', BTN: 'BT', BOL: 'BO', BES: 'BQ', BIH: 'BA', BWA: 'BW', BVT: 'BV', BRA: 'BR',
  IOT: 'IO', BRN: 'BN', BGR: 'BG', BFA: 'BF', BDI: 'BI', CPV: 'CV', KHM: 'KH', CMR: 'CM',
  CAN: 'CA', CYM: 'KY', CAF: 'CF', TCD: 'TD', CHL: 'CL', CHN: 'CN', CXR: 'CX', CCK: 'CC',
  COL: 'CO', COM: 'KM', COG: 'CG', COD: 'CD', COK: 'CK', CRI: 'CR', CIV: 'CI', HRV: 'HR',
  CUB: 'CU', CUW: 'CW', CYP: 'CY', CZE: 'CZ', DNK: 'DK', DJI: 'DJ', DMA: 'DM', DOM: 'DO',
  ECU: 'EC', EGY: 'EG', SLV: 'SV', GNQ: 'GQ', ERI: 'ER', EST: 'EE', SWZ: 'SZ', ETH: 'ET',
  FLK: 'FK', FRO: 'FO', FJI: 'FJ', FIN: 'FI', FRA: 'FR', GUF: 'GF', PYF: 'PF', ATF: 'TF',
  GAB: 'GA', GMB: 'GM', GEO: 'GE', DEU: 'DE', GHA: 'GH', GIB: 'GI', GRC: 'GR', GRL: 'GL',
  GRD: 'GD', GLP: 'GP', GUM: 'GU', GTM: 'GT', GGY: 'GG', GIN: 'GN', GNB: 'GW', GUY: 'GY',
  HTI: 'HT', HMD: 'HM', VAT: 'VA', HND: 'HN', HKG: 'HK', HUN: 'HU', ISL: 'IS', IND: 'IN',
  IDN: 'ID', IRN: 'IR', IRQ: 'IQ', IRL: 'IE', IMN: 'IM', ISR: 'IL', ITA: 'IT', JAM: 'JM',
  JPN: 'JP', JEY: 'JE', JOR: 'JO', KAZ: 'KZ', KEN: 'KE', KIR: 'KI', PRK: 'KP', KOR: 'KR',
  KWT: 'KW', KGZ: 'KG', LAO: 'LA', LVA: 'LV', LBN: 'LB', LSO: 'LS', LBR: 'LR', LBY: 'LY',
  LIE: 'LI', LTU: 'LT', LUX: 'LU', MAC: 'MO', MDG: 'MG', MWI: 'MW', MYS: 'MY', MDV: 'MV',
  MLI: 'ML', MLT: 'MT', MHL: 'MH', MTQ: 'MQ', MRT: 'MR', MUS: 'MU', MYT: 'YT', MEX: 'MX',
  FSM: 'FM', MDA: 'MD', MCO: 'MC', MNG: 'MN', MNE: 'ME', MSR: 'MS', MAR: 'MA', MOZ: 'MZ',
  MMR: 'MM', NAM: 'NA', NRU: 'NR', NPL: 'NP', NLD: 'NL', NCL: 'NC', NZL: 'NZ', NIC: 'NI',
  NER: 'NE', NGA: 'NG', NIU: 'NU', NFK: 'NF', MKD: 'MK', MNP: 'MP', NOR: 'NO', OMN: 'OM',
  PAK: 'PK', PLW: 'PW', PSE: 'PS', PAN: 'PA', PNG: 'PG', PRY: 'PY', PER: 'PE', PHL: 'PH',
  PCN: 'PN', POL: 'PL', PRT: 'PT', PRI: 'PR', QAT: 'QA', REU: 'RE', ROU: 'RO', RUS: 'RU',
  RWA: 'RW', BLM: 'BL', SHN: 'SH', KNA: 'KN', LCA: 'LC', MAF: 'MF', SPM: 'PM', VCT: 'VC',
  WSM: 'WS', SMR: 'SM', STP: 'ST', SAU: 'SA', SEN: 'SN', SRB: 'RS', SYC: 'SC', SLE: 'SL',
  SGP: 'SG', SXM: 'SX', SVK: 'SK', SVN: 'SI', SLB: 'SB', SOM: 'SO', ZAF: 'ZA', SGS: 'GS',
  SSD: 'SS', ESP: 'ES', LKA: 'LK', SDN: 'SD', SUR: 'SR', SJM: 'SJ', SWE: 'SE', CHE: 'CH',
  SYR: 'SY', TWN: 'TW', TJK: 'TJ', TZA: 'TZ', THA: 'TH', TLS: 'TL', TGO: 'TG', TKL: 'TK',
  TON: 'TO', TTO: 'TT', TUN: 'TN', TUR: 'TR', TKM: 'TM', TCA: 'TC', TUV: 'TV', UGA: 'UG',
  UKR: 'UA', ARE: 'AE', GBR: 'GB', USA: 'US', UMI: 'UM', URY: 'UY', UZB: 'UZ', VUT: 'VU',
  VEN: 'VE', VNM: 'VN', VGB: 'VG', VIR: 'VI', WLF: 'WF', ESH: 'EH', YEM: 'YE', ZMB: 'ZM',
  ZWE: 'ZW',
}

/** Three letters, as the API demands for a corrected nationality (#1044). */
export const NATIONALITY_ALPHA3_PATTERN = /^[A-Za-z]{3}$/

/**
 * The alpha-2 code of a country, whether it arrives as alpha-2 or alpha-3.
 *
 * `null` for everything that is neither — an empty value or a format we do not
 * know is never guessed at.
 */
function alpha2FromCode(code?: string | null): string | null {
  const normalized = code?.trim().toUpperCase() ?? ''
  if (normalized.length === 3) return COUNTRY_ALPHA3_TO_ALPHA2[normalized] ?? null
  return normalized.length === 2 ? normalized : null
}

/**
 * Category for a country code from the identification.
 *
 * Accepts both notations: the ident provider sends alpha-2, the document read
 * by our own AI alpha-3 (`CHE`, `DEU`). Returns `null` for anything we cannot
 * map with certainty — the dialog then asks the question instead of guessing.
 */
export function nationalityCategoryFromCode(code?: string | null): NationalityCategory | null {
  const alpha2 = alpha2FromCode(code)
  if (!alpha2) return null
  if (alpha2 === NATIONALITY_CATEGORY.CH) return NATIONALITY_CATEGORY.CH
  if (EU_EFTA_COUNTRY_CODES.includes(alpha2)) return NATIONALITY_CATEGORY.EU_EFTA
  return NATIONALITY_CATEGORY.OTHER
}

/**
 * The country of a code in plain text, e.g. `DEU` → «Deutschland».
 *
 * Falls back to the code itself whenever it cannot be resolved — a reviewer
 * must never lose the value just because a country is unknown to us.
 */
export function countryNameFromCode(code?: string | null, locale = 'de'): string | null {
  const normalized = code?.trim().toUpperCase() ?? ''
  if (!normalized) return null
  const alpha2 = alpha2FromCode(normalized)
  if (!alpha2) return normalized
  try {
    return new Intl.DisplayNames([locale], { type: 'region' }).of(alpha2) ?? normalized
  } catch {
    return normalized
  }
}
